import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MyButton from "../../../components/MyButton";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { AuthContext } from "../../../context/AuthProvider";
import { PopupContext } from "../../../context/PopupProvider";
import DeleteBoardItemDialog from "../../Common/DeleteBoardItemDialog";
import MyLoading from "../../../components/MyLoading";
import { getStatusLabel, renderBody } from "../../../utils";
import MyTitle from "../../../components/MyTitle";
import FileItem from "../../../components/inputs/MyInputFile/FileItem";
import ResearchComment04 from "../Comment/ResearchComment04";

const ResearchDetail04 = ({ boardCode }) => {
  const { itemNo } = useParams();
  const { useGetBoardItem, useGetBoard } = useBoardItem();
  const navigate = useNavigate();
  const { profile, isAuthenticated, isAdmin } = useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);

  const [replyMode, setReplyMode] = useState(false);

  const { data: board } = useGetBoard({ boardCode }, (data) => data.data);

  const { data, isLoading, isError, error } = useGetBoardItem(
    { boardCode: board?.boardCode, itemNo, includeAdjacent: true },
    (data) => data.data
  );

  const openDeleteDialog = () =>
    openPopup(
      <DeleteBoardItemDialog
        boardCode={board?.boardCode}
        itemNo={itemNo}
        onRedirect={() => {
          navigate(`/Research/QnA`);
        }}
      />
    );

  return (
    <div>
      {isLoading ? (
        <MyLoading className="h-[150px]" />
      ) : isError && !data ? (
        <p className="text-center text-lg text-gray-dark pt-10">
          {error?.data?.code === "NO_PERMISSION"
            ? "이 글은 비밀글입니다. 작성자 및 관리자만 열람하실 수 있습니다."
            : "이 게시물의 정보를 볼 수 없습니다."}
        </p>
      ) : (
        <>
          <MyTitle title="내용보기" className="mb-4" />

          <div className="border-t-2 border-primary overflow-x-auto max-w-full">
            <table className="w-full border-collapse mb-5">
              <tbody>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    제목
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.title || "-"}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    작성자
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.writer?.name || "-"}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    작성일
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {dayjs(data?.createdDate).format("YYYY-MM-DD")}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    진행상태
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {getStatusLabel(
                      data?.dateOfFirstComment ? "replied" : "waiting",
                      "2xl:text-base text-sm"
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    내용
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.body ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: renderBody(data.body, data.embeddedFiles),
                        }}
                        className="body-text"
                      />
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    첨부파일
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.attachedFiles?.length > 0 ? (
                      <div>
                        {data.attachedFiles.map((item) => (
                          <FileItem key={item.fileId} file={item} />
                        ))}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    동영상
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.additionalItems?.videoUrl ? (
                      <iframe
                        height={315}
                        width={560}
                        title="Video Player"
                        src={data.additionalItems.videoUrl}
                        frameBorder="0"
                        allow="accelerometer; autoplay; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-auto bg-black"
                        style={{
                          aspectRatio: 16 / 9,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="flex items-center mt-10 sm:gap-4 gap-2">
        <MyButton
          variant="contained"
          color="#111111"
          className="font-semibold sm:min-w-[120px] mr-auto text-white"
          onClick={() => navigate(`/Research/QnA`)}
        >
          목록보기
        </MyButton>

        {isAuthenticated &&
        ((profile?.userId === data?.writer?.userId &&
          !data?.dateOfFirstComment) ||
          isAdmin) ? (
          <>
            <MyButton
              variant="outlined"
              className="sm:min-w-[120px]"
              onClick={openDeleteDialog}
            >
              <span className="font-semibold">삭제</span>
            </MyButton>
            <MyButton
              variant="outlined"
              className="sm:min-w-[120px]"
              onClick={() => navigate(`/Research/QnA/Edit/${data?.itemNo}`)}
            >
              <span className="font-semibold">수정</span>
            </MyButton>
          </>
        ) : (
          <></>
        )}
        {isAuthenticated && !replyMode ? (
          <MyButton
            variant="contained"
            color="primary"
            className="sm:min-w-[120px] font-semibold"
            onClick={() => {
              setReplyMode(true);
              const form = document.getElementById("reply-form");

              if (form) {
                form.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            답변하기
          </MyButton>
        ) : (
          <></>
        )}
      </div>

      <ResearchComment04
        board={board}
        boardItem={data}
        className="mt-10"
        replyMode={replyMode}
        setReplyMode={setReplyMode}
      />
    </div>
  );
};

export default ResearchDetail04;
